import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Container, Row, Col } from 'react-bootstrap';
import PricingCard from './components/PricingCard';
import TrepHeader from './components/TrepHeader';
import CommunityProgram from './components/CommunityProgram';
import { PROD_SPECS } from './data/prod-specs';

import { InsertTemplate } from '@styled-icons/icomoon';
import { Workflow } from '@styled-icons/octicons';
import { Security } from '@styled-icons/material-rounded';
import { Shield } from '@styled-icons/fa-solid';
import { CloudUpload } from '@styled-icons/material';
import { PeopleTeam } from '@styled-icons/fluentui-system-filled';

import emailjs from '@emailjs/browser';
import './styles/App.css';

function RequestAccessModal({ show, onClose, title = 'Request Access', subtitle = "Fill out the form and we'll get you started." }) {
  const [formData, setFormData] = useState({
    fullName: '',
    workEmail: '',
    companyName: '',
    roleTitle: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleClose = useCallback(() => {
    setFormData({ fullName: '', workEmail: '', companyName: '', roleTitle: '', message: '' });
    setErrors({});
    setSubmitted(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!show) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [show, handleClose]);

  if (!show) return null;

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.workEmail.trim()) {
      newErrors.workEmail = 'Work email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.workEmail)) {
      newErrors.workEmail = 'Please enter a valid email address';
    }
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setSending(true);
    emailjs.send('service_uo3jp9u', 'template_h9l12fn', {
      full_name: formData.fullName,
      work_email: formData.workEmail,
      company_name: formData.companyName,
      role_title: formData.roleTitle,
      message: formData.message,
    }, '6L3RLPZ8rg0yWc0rw')
    .then(() => {
      setSubmitted(true);
      setTimeout(() => { handleClose(); }, 2000);
    })
    .catch(() => {
      setErrors({ form: 'Something went wrong. Please try again.' });
    })
    .finally(() => {
      setSending(false);
    });
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-access-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={handleClose} aria-label="Close">&times;</button>

        {submitted ? (
          <div className="modal-success">
            <h2>Thank you!</h2>
            <p>We'll be in touch shortly.</p>
          </div>
        ) : (
          <>
            <h2 id="request-access-title" className="modal-title">{title}</h2>
            <p className="modal-subtitle">{subtitle}</p>
            <form className="modal-form" onSubmit={handleSubmit} noValidate>
              <div className="modal-field">
                <label htmlFor="ra-fullName">Full Name *</label>
                <input
                  id="ra-fullName"
                  type="text"
                  value={formData.fullName}
                  onChange={handleChange('fullName')}
                  className={errors.fullName ? 'input-error' : ''}
                />
                {errors.fullName && <span className="field-error">{errors.fullName}</span>}
              </div>
              <div className="modal-field">
                <label htmlFor="ra-workEmail">Work Email *</label>
                <input
                  id="ra-workEmail"
                  type="email"
                  value={formData.workEmail}
                  onChange={handleChange('workEmail')}
                  className={errors.workEmail ? 'input-error' : ''}
                />
                {errors.workEmail && <span className="field-error">{errors.workEmail}</span>}
              </div>
              <div className="modal-field">
                <label htmlFor="ra-companyName">Company Name *</label>
                <input
                  id="ra-companyName"
                  type="text"
                  value={formData.companyName}
                  onChange={handleChange('companyName')}
                  className={errors.companyName ? 'input-error' : ''}
                />
                {errors.companyName && <span className="field-error">{errors.companyName}</span>}
              </div>
              <div className="modal-field">
                <label htmlFor="ra-roleTitle">Role / Title</label>
                <input
                  id="ra-roleTitle"
                  type="text"
                  value={formData.roleTitle}
                  onChange={handleChange('roleTitle')}
                />
              </div>
              <div className="modal-field">
                <label htmlFor="ra-message">Message</label>
                <textarea
                  id="ra-message"
                  rows="3"
                  value={formData.message}
                  onChange={handleChange('message')}
                />
              </div>
              {errors.form && <div className="form-error">{errors.form}</div>}
              <button type="submit" className="modal-submit" disabled={sending}>
                {sending ? 'Sending...' : 'Submit Request'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/community" element={<CommunityProgram />} />
    </Routes>
  );
}

function HomePage() {
  const [showModal, setShowModal] = useState(false);
  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const [showSalesModal, setShowSalesModal] = useState(false);
  const openSalesModal = () => setShowSalesModal(true);
  const closeSalesModal = () => setShowSalesModal(false);

  return (
    <div className="app-container">
      <Helmet>
        <title>teamreports.io - AI Governance Orchestration Platform</title>
        <meta name="description" content="Transform stakeholder input into structured, review-ready, auditable artifacts — powered by AI. Enterprise governance orchestration for intake, review, and publishing." />
        <link rel="icon" href="/favicon.ico" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
      </Helmet>

      <TrepHeader onRequestAccess={openModal} onContactSales={openSalesModal} />

      <RequestAccessModal show={showModal} onClose={closeModal} />
      <RequestAccessModal show={showSalesModal} onClose={closeSalesModal} title="Contact Sales" subtitle="Tell us about your needs and we'll follow up." />

      <main className="main-content">
        {/* Hero Section */}
        <section className="hero-section">
          {/* Floating Graphics */}
          <div className="hero-graphics">
            <div className="floating-card card-1">
              <div className="card-icon">
                <InsertTemplate style={{ width: '20px', height: '20px' }} />
              </div>
              <div className="card-text">Structured Artifact</div>
            </div>

            <div className="floating-card card-2">
              <div className="prompt-card">
                <div className="prompt-icon">
                  <Workflow style={{ width: '16px', height: '16px' }} />
                </div>
                <div className="prompt-text">contributor &rarr; reviewer &rarr; publisher</div>
              </div>
              <div className="ai-arrow">&rarr;</div>
              <div className="form-preview">
                <div className="form-line"></div>
                <div className="form-line short"></div>
                <div className="form-line"></div>
              </div>
            </div>

            <div className="floating-card card-3">
              <div className="checkbox-group">
                <div className="checkbox checked"></div>
                <div className="checkbox-label">Approved</div>
              </div>
            </div>

            <div className="floating-card card-4">
              <div className="card-label">Audit Trail</div>
              <div className="card-select">
                <div className="select-arrow">&#9660;</div>
              </div>
            </div>

            <div className="floating-badge badge-1">
              <Workflow style={{ width: '16px', height: '16px' }} />
              <span>Workflow</span>
            </div>

            <div className="floating-badge badge-2">
              <Shield style={{ width: '16px', height: '16px' }} />
              <span>Secure</span>
            </div>
          </div>

          <Container>
            <div className="hero-content">
              <p className="hero-tension">When reporting becomes fragmented, accountability disappears.</p>
              <h1 className="hero-title">
                From Email Chaos to Governed Decisions
              </h1>
              <p className="hero-subtitle">
                Turn stakeholder input into review-ready artifacts with built-in governance and a full audit trail.
              </p>
              <div className="hero-cta">
                <button className="cta-button primary" onClick={openModal}>Request Access</button>
                <Link to="/community" className="cta-button tertiary">Apply for Community Access</Link>
                <a href="#beforeafter" className="cta-button secondary">See How It Works</a>
              </div>
              <div className="hero-trust-line">
                &#128274; Enterprise-grade governance, authorization &amp; audit controls
              </div>
              <div className="workflow-steps">
                <span className="workflow-step">Contribute</span>
                <span className="workflow-arrow">&rarr;</span>
                <span className="workflow-step">Review</span>
                <span className="workflow-arrow">&rarr;</span>
                <span className="workflow-step">Publish</span>
              </div>
            </div>
          </Container>
        </section>

        {/* Before/After Section */}
        <section className="value-section" id="beforeafter">
          <Container>
            <h2 className="section-title">The Reporting Problem</h2>
            <Row className="align-items-center">
              <Col md={6}>
                <div className="visual-card before">
                  <h4>Conventional Way</h4>
                  <ul>
                    <li>&#10060; Manual data collection across teams</li>
                    <li>&#10060; Email ping-pong and version chaos</li>
                    <li>&#10060; Governance blind spots and approval ambiguity</li>
                  </ul>
                </div>
              </Col>
              <Col md={6}>
                <div className="visual-card after">
                  <h4>With teamreports</h4>
                  <ul>
                    <li>&#9989; AI-powered structured artifact generation</li>
                    <li>&#9989; Controlled multi-stage review workflow</li>
                    <li>&#9989; Single source of truth with full audit trail</li>
                  </ul>
                </div>
              </Col>
            </Row>
            <div className="impact-stats">
              <div className="impact-item">Reduce reporting cycle time by up to 40%*</div>
              <div className="impact-item">Eliminate version ambiguity</div>
              <div className="impact-item">Enforce accountability with role-based approval</div>
            </div>
            <p className="impact-footnote">*Based on typical enterprise reporting cycles</p>
          </Container>
        </section>

        {/* Use Cases Section */}
        <section className="usecases-section" id="usecases">
          <Container>
            <h2 className="section-title">Built for Enterprise Governance at Scale</h2>
            <div className="usecases-grid">
              <div className="usecase-item">AI Initiative Intake</div>
              <div className="usecase-item">Risk &amp; Compliance Reporting</div>
              <div className="usecase-item">Architecture Review Workflows</div>
              <div className="usecase-item">Policy Enforcement Documentation</div>
            </div>
          </Container>
        </section>

        {/* Features Section */}
        <section className="features-section" id="features">
          <Container>
            <h2 className="section-title">Features</h2>

            <Row className="features-grid">
              <Col md={4} className="feature-col">
                <div className="feature-card">
                  <div className="feature-icon-wrapper templates-gradient">
                    <InsertTemplate className="feature-icon" />
                  </div>
                  <h3>Structured Intake &amp; Artifact Generation</h3>
                  <p>AI-assisted templates that transform stakeholder input into standardized, governance-ready artifacts</p>
                </div>
              </Col>

              <Col md={4} className="feature-col">
                <div className="feature-card">
                  <div className="feature-icon-wrapper workflow-gradient">
                    <Workflow className="feature-icon" />
                  </div>
                  <h3>Governance Workflow Engine</h3>
                  <p>Configurable multi-stage approval chains with role-based routing, feedback loops, and full audit trail</p>
                </div>
              </Col>

              <Col md={4} className="feature-col">
                <div className="feature-card">
                  <div className="feature-icon-wrapper auth-gradient">
                    <Shield className="feature-icon" />
                  </div>
                  <h3>Automated Reporting Cycles</h3>
                  <p>Recurring reporting, compliance tracking, initiative reviews &mdash; without manual coordination</p>
                </div>
              </Col>

              <Col md={4} className="feature-col">
                <div className="feature-card">
                  <div className="feature-icon-wrapper security-gradient">
                    <Security className="feature-icon" />
                  </div>
                  <h3>Enterprise-Grade Access Control</h3>
                  <p>Fine-grained RBAC and report-level permissions for sensitive initiatives</p>
                </div>
              </Col>

              <Col md={4} className="feature-col">
                <div className="feature-card">
                  <div className="feature-icon-wrapper collab-gradient">
                    <PeopleTeam className="feature-icon" />
                  </div>
                  <h3>Audit &amp; Traceability</h3>
                  <p>Every action tracked. Full visibility into who contributed, reviewed, and approved &mdash; across every artifact</p>
                </div>
              </Col>

              <Col md={4} className="feature-col">
                <div className="feature-card">
                  <div className="feature-icon-wrapper publish-gradient">
                    <CloudUpload className="feature-icon" />
                  </div>
                  <h3>Cloud-Native</h3>
                  <p>100% serverless architecture. Scales with your organization, not your infrastructure</p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Feature Comparison Matrix */}
        <section className="comparison-section" id="compare">
          <Container>
            <h2 className="section-title">Compare Plans</h2>
            <p className="section-subtitle">See exactly what you get at every tier</p>

            <div className="comparison-group">
              <h3 className="comparison-group-title">Workflow &amp; Governance</h3>
              <div className="comparison-table-wrapper">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Team Pilot</th>
                      <th>Growth</th>
                      <th>Enterprise</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Structured Intake Templates</td>
                      <td>10</td>
                      <td>25</td>
                      <td>Unlimited</td>
                    </tr>
                    <tr>
                      <td>Multi-Stage Workflow</td>
                      <td>Standard</td>
                      <td>Customizable</td>
                      <td>Fully Custom</td>
                    </tr>
                    <tr>
                      <td>Governance Cycle Automation</td>
                      <td className="cell-no">&#10060;</td>
                      <td className="cell-yes">&#9989;</td>
                      <td className="cell-yes">&#9989;</td>
                    </tr>
                    <tr>
                      <td>Role-Based Approval</td>
                      <td className="cell-yes">&#9989;</td>
                      <td className="cell-yes">&#9989;</td>
                      <td>Advanced Routing</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="comparison-group">
              <h3 className="comparison-group-title">Access &amp; Security</h3>
              <div className="comparison-table-wrapper">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Team Pilot</th>
                      <th>Growth</th>
                      <th>Enterprise</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Role-Based Access</td>
                      <td className="cell-yes">&#9989;</td>
                      <td className="cell-yes">&#9989;</td>
                      <td>Fine-Grained</td>
                    </tr>
                    <tr>
                      <td>Report-Level Permissions</td>
                      <td className="cell-no">&#10060;</td>
                      <td className="cell-yes">&#9989;</td>
                      <td>Advanced</td>
                    </tr>
                    <tr>
                      <td>SSO</td>
                      <td className="cell-no">&#10060;</td>
                      <td className="cell-no">&#10060;</td>
                      <td className="cell-yes">&#9989;</td>
                    </tr>
                    <tr>
                      <td>Audit Trail</td>
                      <td>Standard</td>
                      <td>Full audit trail</td>
                      <td>Full audit trail + Export</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="comparison-group">
              <h3 className="comparison-group-title">AI Capabilities</h3>
              <div className="comparison-table-wrapper">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Team Pilot</th>
                      <th>Growth</th>
                      <th>Enterprise</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>AI Artifact Generation</td>
                      <td>Standard</td>
                      <td>Advanced</td>
                      <td>Enterprise</td>
                    </tr>
                    <tr>
                      <td>AI Custom Templates</td>
                      <td className="cell-no">&#10060;</td>
                      <td className="cell-yes">&#9989;</td>
                      <td className="cell-yes">&#9989;</td>
                    </tr>
                    <tr>
                      <td>AI Governance Controls</td>
                      <td className="cell-no">&#10060;</td>
                      <td>Limited</td>
                      <td>Advanced</td>
                    </tr>
                    <tr>
                      <td>AI Credits / Month</td>
                      <td>500 / month (workspace pooled)</td>
                      <td>2,000 / month (workspace pooled)</td>
                      <td>Custom</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="comparison-group">
              <h3 className="comparison-group-title">Data &amp; Retention</h3>
              <div className="comparison-table-wrapper">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Team Pilot</th>
                      <th>Growth</th>
                      <th>Enterprise</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Data Retention</td>
                      <td>6 Months</td>
                      <td>2 Years</td>
                      <td>Unlimited</td>
                    </tr>
                    <tr>
                      <td>Dedicated Domain</td>
                      <td className="cell-yes">&#9989;</td>
                      <td className="cell-yes">&#9989;</td>
                      <td className="cell-yes">&#9989;</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="comparison-group">
              <h3 className="comparison-group-title">Support</h3>
              <div className="comparison-table-wrapper">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Team Pilot</th>
                      <th>Growth</th>
                      <th>Enterprise</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Email Support</td>
                      <td className="cell-yes">&#9989;</td>
                      <td>Priority</td>
                      <td>Dedicated</td>
                    </tr>
                    <tr>
                      <td>SLA</td>
                      <td className="cell-no">&#10060;</td>
                      <td className="cell-no">&#10060;</td>
                      <td className="cell-yes">&#9989;</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Container>
        </section>

        {/* Pricing Section */}
        <section className="pricing-section" id="pricing">
          <Container>
            <h2 className="section-title">Simple, Transparent Pricing</h2>
            <p className="section-subtitle">Choose the plan that fits your team</p>

            <Row className="justify-content-center">
              {PROD_SPECS.map((prod, index) => (
                <Col key={`prod-${index}`} lg={4} md={6} className="pricing-col">
                  <PricingCard {...prod} onCtaClick={openModal} onContactSalesClick={openSalesModal} />
                </Col>
              ))}
            </Row>
            <p className="community-callout">
              Mission-driven organization? &rarr; <Link to="/community">Explore our Community Program</Link>
            </p>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <Container>
            <div className="cta-content">
              <h2>Bring Structure and Accountability to Your Enterprise Reporting</h2>
              <p>Start a Pilot</p>
              <button className="cta-button large" onClick={openModal}>Request Access</button>
            </div>
          </Container>
        </section>
      </main>

      <footer className="footer">
        <Container>
          <div className="footer-content">
            <div className="footer-brand">
              <span className="brand-team">team</span>
              <span className="brand-reports">reports</span>
              <span className="brand-team">.i</span>
              <span className="brand-reports">o</span>
            </div>
            <div className="footer-links">
              <a href="#features">Features</a>
              <a href="#usecases">Use Cases</a>
              <a href="#pricing">Pricing</a>
              <Link to="/community">Community</Link>
              <span className="footer-link-disabled" title="Coming Soon">Docs</span>
            </div>
            <div className="footer-contact">
              &copy; 2026 SBABS Corp. All rights reserved. | reachteamreports@gmail.com
            </div>
          </div>
        </Container>
      </footer>
    </div>
  );
}

export default App;
